<template>
  <b-card no-body>
    <b-card-header class="pb-3">
      <b-row align-v="center">
        <b-col>
          <h3 class="mb-0">Event History</h3>
          <small class="text-muted">Complete history of all beacon events</small>
        </b-col>
        <b-col cols="auto">
          <b-badge variant="light" class="mr-2">
            {{ stableFilteredEvents.length }} of {{ localEvents.length }} events
          </b-badge>
          <b-button 
            variant="outline-secondary" 
            size="sm" 
            @click="handleExport"
            :disabled="stableFilteredEvents.length === 0"
          >
            <i class="fas fa-download"></i> Export
          </b-button>
        </b-col>
      </b-row>

      <!-- Event History Filters -->
      <b-row class="mt-3">
        <b-col md="3">
          <b-form-group label="Event Type" label-size="sm" class="mb-0">
            <b-form-select v-model="localFilters.eventType" size="sm" @change="handleFilterChange">
              <b-form-select-option :value="null">All Events</b-form-select-option>
              <b-form-select-option value="beacon_detected">Detections</b-form-select-option>
              <b-form-select-option value="confirm_visit">Confirmations</b-form-select-option>
              <b-form-select-option value="message_sent">Messages</b-form-select-option>
            </b-form-select>
          </b-form-group>
        </b-col>
        
        <b-col md="3">
          <b-form-group label="Device" label-size="sm" class="mb-0">
            <b-form-select v-model="localFilters.deviceId" size="sm" @change="handleFilterChange">
              <b-form-select-option :value="null">All Devices</b-form-select-option>
              <b-form-select-option 
                v-for="device in uniqueDevicesFromLocal" 
                :key="device"
                :value="device"
              >
                {{ getNurseNameLocal(device) }} ({{ device }})
              </b-form-select-option>
            </b-form-select>
          </b-form-group>
        </b-col>
        
        <b-col md="3">
          <b-form-group label="Patient" label-size="sm" class="mb-0">
            <b-form-select v-model="localFilters.patient" size="sm" @change="handleFilterChange">
              <b-form-select-option :value="null">All Patients</b-form-select-option>
              <b-form-select-option 
                v-for="patient in uniquePatientsFromLocal" 
                :key="patient"
                :value="patient"
              >
                {{ patient }}
              </b-form-select-option>
            </b-form-select>
          </b-form-group>
        </b-col>
        
        <b-col md="3">
          <b-form-group label="Date Range" label-size="sm" class="mb-0">
            <b-form-select v-model="localFilters.dateRange" size="sm" @change="handleFilterChange">
              <b-form-select-option :value="null">All Time</b-form-select-option>
              <b-form-select-option value="1">Last 24 Hours</b-form-select-option>
              <b-form-select-option value="7">Last 7 Days</b-form-select-option>
              <b-form-select-option value="30">Last 30 Days</b-form-select-option>
            </b-form-select>
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Active History Filters -->
      <b-row v-if="hasActiveFilters" class="mt-2">
        <b-col>
          <div class="d-flex align-items-center">
            <small class="text-muted mr-2">Active filters:</small>
            <b-badge 
              v-if="localFilters.eventType" 
              variant="primary" 
              class="mr-1"
              @click="clearFilter('eventType')"
              style="cursor: pointer;"
            >
              {{ getEventTypeLabel(localFilters.eventType) }}
              <i class="fas fa-times ml-1"></i>
            </b-badge>
            <b-badge 
              v-if="localFilters.deviceId" 
              variant="info" 
              class="mr-1"
              @click="clearFilter('deviceId')"
              style="cursor: pointer;"
            >
              Device: {{ getNurseNameLocal(localFilters.deviceId) }}
              <i class="fas fa-times ml-1"></i>
            </b-badge>
            <b-badge 
              v-if="localFilters.patient" 
              variant="success" 
              class="mr-1"
              @click="clearFilter('patient')"
              style="cursor: pointer;"
            >
              Patient: {{ localFilters.patient }}
              <i class="fas fa-times ml-1"></i>
            </b-badge>
            <b-badge 
              v-if="localFilters.dateRange" 
              variant="warning" 
              class="mr-1"
              @click="clearFilter('dateRange')"
              style="cursor: pointer;"
            >
              {{ getDateRangeLabel(localFilters.dateRange) }}
              <i class="fas fa-times ml-1"></i>
            </b-badge>
            <b-button 
              variant="link" 
              size="sm" 
              class="ml-auto p-0"
              @click="clearAllFilters"
            >
              Clear All
            </b-button>
          </div>
        </b-col>
      </b-row>
    </b-card-header>
    
    <!-- Event Table -->
    <div v-if="loading && localEvents.length === 0" class="text-center p-4">
      <b-spinner variant="primary"></b-spinner>
      <div>Loading events...</div>
    </div>

    <div v-else-if="stableFilteredEvents.length === 0 && localEvents.length > 0" class="text-center py-5">
      <i class="fas fa-filter fa-3x text-muted mb-3"></i>
      <p class="text-muted">No events match your current filters</p>
      <b-button variant="outline-primary" size="sm" @click="clearAllFilters">
        Clear Filters
      </b-button>
    </div>

    <div v-else-if="localEvents.length === 0 && !loading" class="text-center py-5">
      <i class="fas fa-inbox fa-3x text-muted mb-3"></i>
      <p class="text-muted">No events found in history</p>
    </div>

    <div v-else class="table-responsive" :key="stableTableKey">
      <table class="table table-hover table-sm">
        <thead class="thead-light">
          <tr>
            <th width="180">Time</th>
            <th width="150">Event Type</th>
            <th width="150">Patient</th>
            <th width="180">Nurse/Device</th>
            <th width="120">Location</th>
            <th width="150">Beacon MAC</th>
            <th width="100">Signal</th>
            <th>Medications</th>
          </tr>
        </thead>
        <tbody>
          <tr 
            v-for="event in stableFilteredEvents" 
            :key="getEventKey(event)"
          >
            <td><strong>{{ formatTime(event.timestamp) }}</strong></td>
            <td>
              <b-badge :variant="getEventTypeBadgeVariant(event.event_type)">
                <i :class="getEventIcon(event)"></i>
                {{ getEventTypeLabel(event.event_type) }}
              </b-badge>
            </td>
            <td>{{ event.patient || 'Unknown' }}</td>
            <td>
              <div>
                <div class="font-weight-bold">{{ event.assigned_nurse || event.device_id }}</div>
                <small class="text-muted">{{ event.device_id }}</small>
              </div>
            </td>
            <td>{{ event.room || 'N/A' }}</td>
            <td>{{ event.beacon_mac || 'N/A' }}</td>
            <td>
              <b-badge 
                v-if="event.rssi" 
                :variant="getRssiBadgeVariant(event.rssi)"
              >
                {{ event.rssi }} dBm
              </b-badge>
              <span v-else>-</span>
            </td>
            <td>
              <b-badge
                v-for="(med, idx) in event.medications"
                :key="idx"
                variant="info"
                class="mr-1 mb-1"
              >
                {{ med }}
              </b-badge>
              <span v-if="!event.medications || event.medications.length === 0">-</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </b-card>
</template>

<script>
export default {
  name: 'EventHistory',
  props: {
    allEvents: {
      type: Array,
      default: () => []
    },
    historyFilters: {
      type: Object,
      default: () => ({
        eventType: null,
        deviceId: null,
        patient: null,
        dateRange: null
      })
    },
    loading: {
      type: Boolean,
      default: false
    },
    uniqueDevices: {
      type: Array,
      default: () => []
    },
    uniquePatients: {
      type: Array,
      default: () => []
    },
    // Helper functions
    formatTime: {
      type: Function,
      required: true
    },
    getNurseName: {
      type: Function,
      required: true
    },
    getEventTypeLabel: {
      type: Function,
      required: true
    },
    getEventTypeBadgeVariant: {
      type: Function,
      required: true
    },
    getRssiBadgeVariant: {
      type: Function,
      required: true
    }
  },
  data() {
    return {
      // Local copy of events that we update incrementally
      localEvents: [],
      // Map for quick lookup by unique key
      eventMap: new Map(),
      // Local copy of filters for two-way binding
      internalFilters: { ...this.historyFilters },
      // Cache for stable filtered events
      cachedFilteredEvents: null,
      cachedFilterHash: '',
      // Stable key for table to prevent unnecessary re-renders
      stableTableKey: 0,
      // Track last update to detect changes
      lastUpdateTime: 0,
      // Cache for event keys to avoid recomputation
      eventKeyCache: new WeakMap()
    }
  },
  computed: {
    localFilters: {
      get() {
        return this.internalFilters;
      },
      set(value) {
        this.internalFilters = value;
        this.$emit('update:filters', value);
      }
    },
    hasActiveFilters() {
      return this.localFilters.eventType || this.localFilters.deviceId || 
             this.localFilters.patient || this.localFilters.dateRange;
    },
    // Get unique devices from local events
    uniqueDevicesFromLocal() {
      const devices = [...new Set(this.localEvents.map(event => event.device_id))];
      return devices.filter(device => device).sort();
    },
    // Get unique patients from local events
    uniquePatientsFromLocal() {
      const patients = [...new Set(this.localEvents.map(event => event.patient))];
      return patients.filter(patient => patient && patient !== 'Unknown' && patient !== 'N/A').sort();
    },
    // Stable version of filtered events - returns same reference when content unchanged
    stableFilteredEvents() {
      // Create hash of current state
      const stateHash = this.createStateHash();
      
      // Return cached result if state hasn't changed
      if (this.cachedFilterHash === stateHash && this.cachedFilteredEvents) {
        return this.cachedFilteredEvents;
      }
      
      // Compute fresh filtered events
      const filtered = this.computeFilteredEvents();
      
      // Cache the result
      this.cachedFilteredEvents = filtered;
      this.cachedFilterHash = stateHash;
      
      return filtered;
    }
  },
  watch: {
    // Sync when parent filters change
    historyFilters: {
      handler(newFilters) {
        this.internalFilters = { ...newFilters };
      },
      deep: true
    },
    // Watch for new events from parent - only update if truly new
    allEvents: {
      handler(newEvents, oldEvents) {
        if (newEvents === oldEvents) {
          return; // Same reference, no update needed
        }
        
        // Check if there are actually new events
        const hasNewData = this.detectNewEvents(newEvents);
        
        if (hasNewData) {
          this.processIncrementalUpdate(newEvents);
        }
        // If no new data, do nothing - keep current localEvents
      },
      deep: false, // Only watch array reference changes
      immediate: true // Process initial data
    }
  },
  methods: {
    // Generate a unique key for each event (cached)
    getEventKey(event) {
      // Check cache first
      if (this.eventKeyCache.has(event)) {
        return this.eventKeyCache.get(event);
      }
      
      // Compute key
      const key = `${event.timestamp}_${event.device_id}_${event.beacon_mac || 'none'}_${event.event_type}`;
      
      // Cache it
      this.eventKeyCache.set(event, key);
      
      return key;
    },
    
    // Create hash of current state for caching
    createStateHash() {
      const state = {
        filters: this.localFilters,
        eventsCount: this.localEvents.length,
        // Use first event's timestamp as quick change indicator
        firstEventTime: this.localEvents[0]?.timestamp,
        // Also hash a few sample events to detect content changes
        sampleHash: this.getSampleHash()
      };
      
      return JSON.stringify(state);
    },
    
    // Get hash of sample events to detect content changes
    getSampleHash() {
      if (this.localEvents.length === 0) return '';
      
      // Sample first 3 events
      const sample = this.localEvents.slice(0, 3).map(e => ({
        t: e.timestamp,
        d: e.device_id,
        b: e.beacon_mac,
        n: e.assigned_nurse,
        r: e.rssi
      }));
      
      return JSON.stringify(sample);
    },
    
    // Compute filtered events (without caching)
    computeFilteredEvents() {
      let events = this.localEvents;
      
      // Apply filters
      if (this.localFilters.eventType) {
        events = events.filter(e => e.event_type === this.localFilters.eventType);
      }
      
      if (this.localFilters.deviceId) {
        events = events.filter(e => e.device_id === this.localFilters.deviceId);
      }
      
      if (this.localFilters.patient) {
        events = events.filter(e => e.patient === this.localFilters.patient);
      }
      
      if (this.localFilters.dateRange) {
        const days = parseInt(this.localFilters.dateRange);
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - days);
        
        events = events.filter(e => {
          const eventDate = new Date(e.timestamp);
          return eventDate >= cutoffDate;
        });
      }
      
      return events;
    },
    
    // Detect if there are truly new events
    detectNewEvents(newEvents) {
      if (!newEvents || newEvents.length === 0) {
        return false;
      }
      
      // If we have no local events, all are new
      if (this.localEvents.length === 0) {
        return true;
      }
      
      // Check if first event is new (events are sorted newest first)
      const firstNewEvent = newEvents[0];
      const firstLocalEvent = this.localEvents[0];
      
      if (!firstLocalEvent) {
        return true;
      }
      
      // Compare key properties
      const isNew = firstNewEvent.timestamp !== firstLocalEvent.timestamp ||
                   firstNewEvent.device_id !== firstLocalEvent.device_id ||
                   firstNewEvent.beacon_mac !== firstLocalEvent.beacon_mac ||
                   firstNewEvent.event_type !== firstLocalEvent.event_type;
      
      return isNew;
    },
    
    // Process incremental update - only add events we don't have
    processIncrementalUpdate(newEvents) {
      if (!newEvents || newEvents.length === 0) {
        return;
      }
      
      let addedCount = 0;
      
      // Process events in reverse (oldest to newest) to maintain chronological order
      for (let i = newEvents.length - 1; i >= 0; i--) {
        const event = newEvents[i];
        const key = this.getEventKey(event);
        
        if (!this.eventMap.has(key)) {
          // New event - add to beginning (newest first)
          this.localEvents.unshift(event);
          this.eventMap.set(key, event);
          addedCount++;
        }
      }
      
      // Limit total events to prevent memory issues
      const MAX_EVENTS = 200;
      if (this.localEvents.length > MAX_EVENTS) {
        const removed = this.localEvents.splice(MAX_EVENTS);
        removed.forEach(event => {
          const key = this.getEventKey(event);
          this.eventMap.delete(key);
          this.eventKeyCache.delete(event);
        });
      }
      
      // Only update table key if we actually added new events
      if (addedCount > 0) {
        this.stableTableKey++;
       //console.log(`📈 Added ${addedCount} new events`);
      }
    },
    
    // Old updateLocalEvents method - keeping for compatibility but using new logic
    updateLocalEvents(newEvents) {
      this.processIncrementalUpdate(newEvents);
    },
    
    // Local version of getNurseName that uses local events
    getNurseNameLocal(deviceId) {
      // Find most recent event for this device in local data
      const event = this.localEvents.find(e => e.device_id === deviceId);
      return event ? event.assigned_nurse : deviceId;
    },
    
    handleFilterChange() {
      // Emit filter change to parent
      this.$emit('filter-change', this.localFilters);
    },
    
    clearFilter(filterName) {
      this.localFilters = {
        ...this.localFilters,
        [filterName]: null
      };
      this.handleFilterChange();
    },
    
    clearAllFilters() {
      this.localFilters = {
        eventType: null,
        deviceId: null,
        patient: null,
        dateRange: null
      };
      this.handleFilterChange();
    },
    
    handleExport() {
      this.$emit('export', this.stableFilteredEvents);
    },
    
    getEventIcon(event) {
      const icons = {
        beacon_detected: 'fas fa-broadcast-tower',
        confirm_visit: 'fas fa-check-circle',
        message_sent: 'fas fa-paper-plane'
      };
      return icons[event.event_type] || 'fas fa-circle';
    },
    
    getDateRangeLabel(days) {
      const labels = {
        '1': 'Last 24 Hours',
        '7': 'Last 7 Days',
        '30': 'Last 30 Days'
      };
      return labels[days] || `${days} Days`;
    }
  },
  mounted() {
    // Initial load - process any existing events
    if (this.allEvents && this.allEvents.length > 0) {
      this.processIncrementalUpdate(this.allEvents);
      this.stableTableKey = 1; // Initial render
    }
  },
  beforeDestroy() {
    // Cleanup
    this.eventMap.clear();
    this.eventKeyCache = new WeakMap();
  }
}
</script>

<style scoped>
.table-sm td, .table-sm th {
  padding: 0.5rem;
}

.table-hover tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.075);
}

/* Disable all transitions and animations to prevent flicker */
.table-responsive,
.table-responsive *,
table,
table *,
tbody,
tbody *,
tr,
td,
th {
  transition: none !important;
  animation: none !important;
}

/* Ensure smooth rendering */
.table-responsive {
  will-change: transform;
  backface-visibility: hidden;
  transform: translateZ(0);
}

/* Prevent Flash of Unstyled Content */
tbody tr {
  opacity: 1 !important;
  visibility: visible !important;
}
</style>